import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react';
import { getDropdownPosition } from './dropdownPosition';
import { useDropdownDismiss } from './useDropdownDismiss';
import './dropdown.css';

/**
 * Type B (grouped) — a SmartSelect variant whose options sit under group
 * headers, with an optional muted second line per option. Same S2 trigger (the
 * closed trigger becomes the search input when open; the dropdown attaches flush
 * below) and client-side label filtering as SmartSelect, plus arrow/Enter
 * keyboard nav and Tab-to-commit (autoSelect). Use SmartSelect for a flat list;
 * this is for bounded, grouped catalogs (e.g. ticket types).
 */
export interface GroupedSelectOption {
  value: string;
  /** Primary line; also what the search filters on. */
  label: string;
  /** Optional muted second line. */
  description?: string;
  /** Group header this option sits under. */
  group: string;
}

export interface GroupedSelectHandle {
  focus: () => void;
}

interface GroupedSelectProps {
  label: string;
  value: string | null;
  options: GroupedSelectOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Fires after Tab leaves the field (after committing any typed match). */
  onTabAway?: () => void;
  emptyText?: string;
}

export const GroupedSelect = React.forwardRef<GroupedSelectHandle, GroupedSelectProps>(
  function GroupedSelect(
    {
      label,
      value,
      options,
      onChange,
      placeholder = 'Select…',
      disabled,
      onTabAway,
      emptyText = 'No results',
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => {
      setOpen(false);
      setSearch('');
      // Blur so the trigger's :focus-within blue border doesn't linger after the
      // dropdown closes. (The merge chrome is now gated on the --open class.)
      inputRef.current?.blur();
    }, []);

    useDropdownDismiss(dropdownRef, triggerRef, close, open);

    useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);

    const selectedOption = options.find((o) => o.value === value);
    const filtered = options.filter((o) =>
      o.label.toLowerCase().includes(search.toLowerCase()),
    );

    // Keep the highlight in range when the filtered set shrinks.
    useEffect(() => {
      setHighlighted((h) => Math.min(h, Math.max(0, filtered.length - 1)));
    }, [filtered.length]);

    // Keep the highlighted row scrolled into view.
    useEffect(() => {
      if (!open) return;
      const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${highlighted}"]`);
      row?.scrollIntoView({ block: 'nearest' });
    }, [highlighted, open]);

    const handleOpen = () => {
      if (disabled) return;
      setOpen(true);
      setSearch('');
      // Highlight the current selection so it's centred and Enter is a no-op
      // until the user navigates or types.
      const selIdx = options.findIndex((o) => o.value === value);
      setHighlighted(selIdx >= 0 ? selIdx : 0);
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    const commit = (val: string) => {
      onChange(val);
      close();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) {
          handleOpen();
          return;
        }
        setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        if (open && filtered[highlighted]) {
          e.preventDefault();
          commit(filtered[highlighted].value);
        }
      } else if (e.key === 'Tab' && !e.shiftKey) {
        // autoSelect: only commit when the user has actually typed a filter, so
        // tabbing through without typing preserves the current selection.
        if (open && search.trim() && filtered[highlighted]) {
          onChange(filtered[highlighted].value);
        }
        close();
        if (onTabAway) setTimeout(() => onTabAway(), 0);
      }
    };

    // Bucket the filtered options into groups in first-appearance order, keeping
    // each option's flat index for keyboard highlight/active state.
    const groups: { group: string; items: { opt: GroupedSelectOption; index: number }[] }[] = [];
    filtered.forEach((opt, index) => {
      let g = groups.find((x) => x.group === opt.group);
      if (!g) {
        g = { group: opt.group, items: [] };
        groups.push(g);
      }
      g.items.push({ opt, index });
    });

    return (
      <div className="sm4rt-select" ref={triggerRef}>
        <div className={`sm4rt-select__trigger${open ? ' sm4rt-select__trigger--open' : ''}`} onClick={!open ? handleOpen : undefined}>
          <span className="sm4rt-select__trigger-label">{label}</span>

          <input
            ref={inputRef}
            className="sm4rt-select__value"
            value={open ? search : selectedOption?.label ?? ''}
            placeholder={open ? 'Search…' : placeholder}
            onChange={(e) => {
              setSearch(e.target.value);
              setHighlighted(0);
            }}
            onFocus={handleOpen}
            onKeyDown={handleKeyDown}
            readOnly={!open}
            disabled={disabled}
          />

          {value && !open && (
            <button
              type="button"
              className="sm4rt-select__clear"
              aria-label="Clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <IconX size={11} />
            </button>
          )}

          <IconChevronDown
            size={12}
            className={`sm4rt-select__chevron ${open ? 'sm4rt-select__chevron--open' : ''}`}
          />
        </div>

        {open &&
          triggerRef.current &&
          createPortal(
            <div
              ref={dropdownRef}
              className="sm4rt-select__dropdown"
              style={getDropdownPosition(triggerRef.current, { gap: 0 })}
            >
              <div className="sm4rt-dd__list sm4rt-dd__list--scroll" ref={listRef}>
                {groups.length > 0 ? (
                  groups.map((g) => (
                    <div className="sm4rt-dd__group" key={g.group}>
                      <div className="sm4rt-dd__group-header">{g.group}</div>
                      {g.items.map(({ opt, index }) => (
                        <div
                          key={opt.value}
                          data-index={index}
                          className={`sm4rt-dd__item ${
                            index === highlighted ? 'sm4rt-dd__item--active' : ''
                          }`}
                          onMouseEnter={() => setHighlighted(index)}
                          onMouseDown={(e) => {
                            e.preventDefault(); // keep input focus until commit
                            commit(opt.value);
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <span className="sm4rt-dd__item-code">{opt.label}</span>
                            {opt.description ? (
                              <span className="sm4rt-dd__item-desc">{opt.description}</span>
                            ) : null}
                          </span>
                          {opt.value === value && (
                            <IconCheck className="sm4rt-dd__check" size={12} />
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="sm4rt-dd__empty">{emptyText}</div>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>
    );
  },
);
